import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  ensureEstateReferral,
  ensureEstateReferralCosmos,
} from "../evm/services/referralService";

/**
 * Global, path-agnostic ESTATE referral enforcer.
 *
 * Product rule: EVERY wallet on Coded Estate must be on the ESTATE referral code —
 * old users or new, and regardless of HOW they connected (connect modal, Keplr/Leap,
 * Web3Auth, "submitted" flow, or a restored session on page load). Whenever a wallet
 * account is present in Redux, this checks the keeper and — if the wallet isn't already
 * on ESTATE — force-redeems it (the user still approves the on-chain tx).
 *
 * Mounted once at the app root so it fires for all routes (including /earn, /claim).
 * The service layer dedupes (in-flight guard) and caches (localStorage) so this can
 * safely coexist with the per-connect handlers without ever double-prompting.
 */
export const ReferralEnforcer = () => {
  const account = useSelector((s) => s.auth.account);
  const wallet = useSelector((s) => s.auth.wallet);
  const dispatch = useDispatch();
  // Addresses handled this session — avoids re-entry on unrelated re-renders.
  const handled = useRef(new Set());

  useEffect(() => {
    if (!account) return;
    const key = String(account).toLowerCase();
    if (handled.current.has(key)) return;
    handled.current.add(key);

    (async () => {
      try {
        if (account.startsWith("0x")) {
          if (!window.ethereum) return;
          const { ethers } = await import("ethers");
          const provider = new ethers.BrowserProvider(window.ethereum);
          const signer = await provider.getSigner();
          await ensureEstateReferral(signer, account);
        } else if (account.startsWith("nibi")) {
          await ensureEstateReferralCosmos(account, wallet, dispatch);
        }
      } catch (err) {
        // Non-blocking. Allow a retry (e.g. user rejected the tx) on the next
        // account change / reload; localStorage only marks after a real success.
        handled.current.delete(key);
        console.debug("ESTATE referral enforcement deferred:", err?.message);
      }
    })();
  }, [account, wallet, dispatch]);

  return null;
};

export default ReferralEnforcer;
